/**
 * PATH: packages/ui/src/kanban/components/settings-home/FacePreview.tsx
 * FILE_ID: UI-KANBAN-SETTINGS-FACE-PREVIEW-001
 * PURPOSE: Render one golden KanbanCard against the draft face recipe.
 * ROLE IN ARCHITECTURE: Local-only preview leaf. Phone/desktop is not persisted.
 * MAIN EXPORTS: FacePreview, previewCardFromGolden.
 * NON-RESPONSIBILITIES: Does not save viewport simulation or open a board.
 * NOTES FOR FUTURE AI: Wrap the card in a local preference context so the draft recipe wins.
 */

import { GOLDEN_CARD_FIXTURES } from '../../card-surface/fixtures/goldenCards'
import type { Card, CardStatus } from '../../types'
import {
  KanbanSettingsPreferenceContext,
  type KanbanSettingsPreferenceContextValue,
} from '../../preferences/KanbanSettingsPreferenceContext'
import { KanbanCard } from '../KanbanCard'
import type { KanbanCardFacePrefs } from '../board-view/cardFacePrefs'
import { DEFAULT_CARD_TAB_LAYOUT } from '../card-detail/drawer-sections/cardTabLayout'
import type { DrawerSectionPrefs } from '../card-detail/drawer-sections/drawerSectionPrefs'

function emptySlice<T extends object>(value: T) {
  return {
    value,
    setValue: () => {},
    hydrated: true,
    syncState: 'idle' as const,
    conflict: null,
    error: null,
  }
}

export function previewCardFromGolden(): Card {
  const wire = GOLDEN_CARD_FIXTURES.rich.wire
  return {
    id: String(wire.id ?? 'card-rich'),
    organisation: typeof wire.organisation === 'number' ? wire.organisation : 1,
    lane: String(wire.lane ?? 'lane-doing'),
    title: String(wire.title ?? 'Preview card'),
    summary: typeof wire.summary === 'string' ? wire.summary : '',
    primary_email: typeof wire.primary_email === 'string' ? wire.primary_email : null,
    related_emails: Array.isArray(wire.related_emails) ? (wire.related_emails as string[]) : [],
    related_email_count: typeof wire.related_email_count === 'number' ? wire.related_email_count : 0,
    related_attachment_ids_json: Array.isArray(wire.related_attachment_ids_json)
      ? (wire.related_attachment_ids_json as string[])
      : [],
    attachment_count: typeof wire.attachment_count === 'number' ? wire.attachment_count : 0,
    job_id: null,
    bill: null,
    variation_id: null,
    invoice_id: null,
    quote_id: null,
    confidence: null,
    suggested_lane: null,
    locked_at: null,
    locked_by: null,
    swimlane_user: null,
    status: (typeof wire.status === 'string' ? wire.status : 'OPEN') as CardStatus,
    snoozed_until: null,
    blocked_reason: typeof wire.blocked_reason === 'string' ? wire.blocked_reason : '',
    due_date: typeof wire.due_date === 'string' ? wire.due_date : null,
    start_date: typeof wire.start_date === 'string' ? wire.start_date : null,
    action_date: typeof wire.action_date === 'string' ? wire.action_date : null,
    effort_days: null,
    priority: typeof wire.priority === 'number' ? wire.priority : 0,
    pinned: wire.pinned === true,
    audit_log_json: [],
    sensitive: false,
    sensitivity_reason: '',
    tags: [],
    assigned_user_ids: Array.isArray(wire.assigned_user_ids)
      ? (wire.assigned_user_ids as number[])
      : [],
    comments_count: typeof wire.comments_count === 'number' ? wire.comments_count : 0,
    checklist_summary:
      wire.checklist_summary && typeof wire.checklist_summary === 'object'
        ? (wire.checklist_summary as Card['checklist_summary'])
        : { total: 0, done: 0 },
    created_at: '2026-07-15T00:00:00Z',
    updated_at: typeof wire.updated_at === 'string' ? wire.updated_at : '2026-07-15T00:00:00Z',
    created_by: null,
  }
}

const PREVIEW_CARD = previewCardFromGolden()
const EMPTY_DRAWER: DrawerSectionPrefs = { expanded: {} }

export function FacePreview({
  prefs,
  phone,
}: {
  prefs: KanbanCardFacePrefs
  phone: boolean
}) {
  const runtime: KanbanSettingsPreferenceContextValue = {
    boardId: '',
    cardFace: emptySlice(prefs),
    cardTabLayout: emptySlice(DEFAULT_CARD_TAB_LAYOUT),
    drawerSections: emptySlice(EMPTY_DRAWER),
  }
  return (
    <div
      data-testid="settings-face-preview-card"
      className={['pointer-events-none', phone ? 'max-w-[16rem]' : ''].join(' ')}
    >
      <KanbanSettingsPreferenceContext.Provider value={runtime}>
        <KanbanCard
          card={PREVIEW_CARD}
          density="compact"
          draggable={false}
        />
      </KanbanSettingsPreferenceContext.Provider>
    </div>
  )
}
